import styled from '@emotion/styled';
import {
    InputAutoSuggestWithLabelProps,
    StyledSuggestionButtonProps,
    StyledIconButtonProps,
    StyledSearchLabelProps
} from './InputAutoSuggestWithLabel.types';
import { css } from '@emotion/react';
import { Text } from '@blueprint/text';
import { PinFillSmall } from '@blueprint/icons';
import { StyledInputBasic } from '@blueprint/text-input';

export const iconButtonSize = '3.375rem';

export const StyledWrapper = styled('div')`
    position: relative;
`;

export const StyledInput = styled(StyledInputBasic)<
    InputAutoSuggestWithLabelProps & { hasSuggestions: boolean } & {
        showError?: boolean;
    } & {
        hasDarkPurpleBorder?: boolean;
    }
>((props) => {
    const {
        theme: { borderRadius, type, spacing, input, colors }
    } = props;

    const borderRightRadius = props.hasBorderRight
        ? `${borderRadius['300']}`
        : `0`;

    const darkPurpleBorderColor = props.hasDarkPurpleBorder
        ? `${props.theme.input.borderColorSearch}`
        : `${input.borderColor}`;

    const borderColor = props.showError
        ? `${props.theme.input.invalidBorderColor}`
        : `${darkPurpleBorderColor}`;

    return css`
        border-top: 1px solid ${borderColor};
        border-bottom: 1px solid ${borderColor};
        border-left: 1px solid ${borderColor};
        border-right: 1px solid
            ${props.hasDarkPurpleBorder ? `${colors.white}` : `${borderColor}`};
        border-right-width: ${props.hasBorderRight || props.showError
            ? `1px`
            : `0`};
        border-radius: ${borderRadius['300']} ${borderRightRadius}
            ${borderRightRadius} ${borderRadius['300']};
        font-weight: ${type.semiBold};
        height: ${props.hasExtendedHeight ? `4.875rem` : `3.375rem`};
        padding: 1.625rem calc(${spacing['48px']} + 1px) ${spacing['8px']}
            0.75rem;
        position: relative;
        overflow: hidden;
        text-overflow: ellipsis;
        &:focus,
        &:hover {
            z-index: 3;
            border-right-width: 1px;
            background-color: ${props.hasDarkPurpleBorder
                ? `${input.hoverBackgroundColor}`
                : `${props.theme.colors.white}`};
            padding-right: ${spacing['48px']};
        }
        &:hover:not(:focus) {
            box-shadow: ${props.hasDarkPurpleBorder
                ? `none`
                : `0 0 0 1px ${input.hoverBorderColor}`};
        }
    `;
});

export const StyledList = styled('ul')((props) => {
    const {
        theme: { borderRadius, colors, spacing, mq }
    } = props;
    return css`
        width: 100%;
        margin: ${spacing['8px']} 0 0 0;
        padding: ${spacing['8px']} 0;
        border-radius: ${borderRadius['300']};
        border: 1px solid ${colors['neutral-3']};
        list-style: none;
        overflow: scroll;
        position: absolute;
        z-index: 1;
        background-color: ${colors.white};
        height: 260px;
        box-shadow: 0 0 12px rgba(50, 39, 68, 0.08),
            0 0 1px rgba(50, 39, 68, 0.32),
            0 8px 16px -8px rgba(50, 39, 68, 0.2);

        ${mq.medium} {
            height: auto;
        }
    `;
});

export const StyledListItem = styled('li')((props) => {
    return css`
        padding: 0;
        color: ${props.theme.colors['text-body']};
    `;
});

export const StyledSuggestionButton = styled(
    'button'
)<StyledSuggestionButtonProps>((props) => {
    const {
        isFocused,
        theme: { colors, spacing, borderRadius, type, fontWeight, buttons }
    } = props;

    return css`
        width: 100%;
        height: 100%;
        min-height: 2.625rem;
        line-height: 1.625rem;
        padding: ${spacing['8px']} ${spacing['24px']};
        text-align: left;
        font-size: ${type.body};
        font-family: inherit;
        appearance: none;
        border: none;
        background: ${isFocused ? colors['neutral-1'] : colors['white']};
        cursor: pointer;
        color: ${isFocused
            ? buttons.tertiary.hoverColor
            : buttons.tertiary.color};
        display: flex;

        strong {
            font-weight: ${fontWeight.semiBold};
            color: ${colors['primary-dark']};
        }

        &:focus,
        &:hover {
            background-color: ${colors['neutral-1']};
            color: ${buttons.tertiary.hoverColor};
        }

        &:focus {
            outline: none;
        }

        &:hover:last-child {
            border-radius: 0 0 ${borderRadius['300']} ${borderRadius['300']};
        }
    `;
});

export const StyledButtonText = styled('span')(css`
    display: inline-block;
`);

export const StyledIconButton = styled('button')<StyledIconButtonProps>(
    (props) => {
        const {
            theme: { colors }
        } = props;

        return css`
            position: absolute;
            top: ${props.hasExtendedHeight ? `0.8rem` : `0`};
            right: 0;
            height: ${iconButtonSize};
            width: ${iconButtonSize};
            text-align: center;
            appearance: none;
            border: none;
            background: none;
            cursor: pointer;
            padding: 0;
            z-index: 3;
            color: ${colors['neutral-5']};
            display: flex;
            align-items: center;
            justify-content: center;

            &:focus {
                outline: none;
            }
        `;
    }
);

export const StyledValue = styled('span')(
    (props) => css`
        display: inline-block;
        width: ${props.theme.spacing['64px']};
    `
);

export const LocationPinIcon = styled(PinFillSmall)((props) => {
    return css`
        margin-right: ${props.theme.spacing['8px']};
        position: relative;
        top: 0.1875rem;
        color: currentColor;
        min-width: ${props.theme.spacing['24px']};
    `;
});

export const StyledSearchLabel = styled(Text)<StyledSearchLabelProps>(
    (props) => {
        return css`
            padding: 0;
            font-weight: ${props.theme.type.semiBold};
            line-height: 0.9375;
            left: 0.75rem;
            pointer-events: none;
            font-size: ${props.theme.type.small};
            color: ${props.showError
                ? `${props.theme.colors['function-error']}`
                : `${props.theme.input.labelColor}`};
            user-select: none;
            position: absolute;
            top: ${props.hasExtendedHeight
                ? `1.25rem`
                : `${props.theme.spacing['8px']}`};
            right: ${props.theme.spacing['8px']};
            z-index: 3;
            ${StyledInput}:focus ~ & {
                color: ${props.theme.input.activeLabelColor};
            }
        `;
    }
);
